import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IDetalleAlerta } from 'modelosSeguridad/src';
import { MaterialModule } from '../../../../auxiliares/material.module';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { IntegracionesService } from '../../../../auxiliares/servicios/http/integraciones.service';
import { CardHeaderComponent } from '../../../../componentes/card-header/card-header.component';

interface DialogData {
  idAlerta: string;
}

/**
 * El informe es un HTML que arma la API de seguridad con datos que carga el
 * vecino, y un blob: tiene el origen de la app. Sin allow-same-origin el
 * informe corre con un origen opaco: sus scripts (si los tiene) funcionan pero
 * no llegan a la sesión ni al sessionStorage de la app (XSS almacenado).
 */
const SANDBOX_INFORME =
  'allow-scripts allow-popups allow-popups-to-escape-sandbox';

@Component({
  selector: 'app-preview-alerta-seguridad',
  templateUrl: './preview-alerta-seguridad.component.html',
  styleUrl: './preview-alerta-seguridad.component.scss',
  standalone: true,
  imports: [CommonModule, MaterialModule, CardHeaderComponent],
})
export class PreviewAlertaSeguridadComponent implements OnInit, OnDestroy {
  data = inject<DialogData>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<PreviewAlertaSeguridadComponent>>(MatDialogRef);
  private service = inject(IntegracionesService);
  private helper = inject(HelperService);
  private sanitizer = inject(DomSanitizer);

  public loading = true;
  public detalle: IDetalleAlerta | null = null;
  public iframeUrl: SafeResourceUrl | null = null;
  public tieneAudio = false;

  private blobUrl: string | null = null;

  get titulo(): string {
    const nombre = this.detalle?.alerta?.configVecino?.datosPersonales?.nombre;
    return nombre ? `Alerta de: ${nombre}` : 'Informe de alerta';
  }

  public cerrar(): void {
    this.dialogRef.close();
  }

  public verInformeEnPestania(): void {
    if (!this.blobUrl) return;
    // Abrir el blob directo le daría el origen de la app a los scripts del
    // informe: se muestra adentro de un iframe aislado, igual que en el diálogo.
    const pestania = window.open('', '_blank');
    if (!pestania) return;
    const doc = pestania.document;
    doc.title = this.titulo;
    doc.body.style.margin = '0';
    const iframe = doc.createElement('iframe');
    iframe.setAttribute('sandbox', SANDBOX_INFORME);
    iframe.style.cssText =
      'position: fixed; inset: 0; width: 100%; height: 100%; border: 0;';
    iframe.src = this.blobUrl;
    doc.body.appendChild(iframe);
  }

  public async descargarAudio(): Promise<void> {
    if (!this.detalle?.alerta?._id) return;
    this.loading = true;
    try {
      await this.service.exportarAudioAlerta(this.detalle.alerta._id);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  private checkAudios(): void {
    this.tieneAudio =
      this.detalle?.multimedia?.some((media) => media.tipo === 'audio') ||
      false;
  }

  private async cargarDetalle(): Promise<void> {
    this.detalle = await this.service.getAlertaById(this.data.idAlerta);
    this.checkAudios();
  }

  private async cargarInforme(): Promise<void> {
    const url = await this.service.getInformeAlertaHtmlUrl(this.data.idAlerta);
    if (!url) return;
    this.blobUrl = url;
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    try {
      await this.cargarDetalle();
      await this.cargarInforme();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  ngOnDestroy(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
  }
}
